/**
 * QueryClient global do TanStack React Query para o front-delivai v2.
 *
 * Defaults pensados para o padrão de uso DelivAI:
 *   - listas grandes (pedidos, conversas) com polling 12s + SSE
 *   - mutations otimistas em status de pedido
 *   - reconexão silenciosa após perda de rede
 *
 * Uso: já injetado em <App /> via <QueryClientProvider client={queryClient}>.
 */
import { QueryClient } from "@tanstack/react-query";
import { ApiClientError } from "./errors";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: "always",
      refetchOnReconnect: true,
      retry: (failureCount: number, error: Error) => {
        if (error instanceof ApiClientError) {
          if (error.retriesExhausted) return false;
          if (error.isUnauthorized() || error.isForbidden() || error.isNotFound()) {
            return false;
          }
          if (error.isValidation()) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: 0,
    },
  },
});

export const queryKeys = {
  orders: (cnpj: string, filters?: Record<string, unknown>) =>
    ["orders", cnpj, filters ?? {}] as const,
  company: (cnpj: string) => ["company", cnpj] as const,
  cashSession: (cnpj: string) => ["cash-session", cnpj] as const,
  employees: (cnpj: string) => ["employees", cnpj] as const,
  consumers: (cnpj: string) => ["consumers", cnpj] as const,
  templates: (niche: string) => ["templates", niche] as const,
  appointments: (cnpj: string, date?: string) =>
    ["appointments", cnpj, date ?? null] as const,
  pageVerify: (cnpj: string) => ["page-verify", cnpj] as const,
  botStats: (cnpj: string) => ["bot-stats", cnpj] as const,
};
